/**
 * The chat-input "Create" menu — the always-available way to request an early
 * Advice artifact (a practice, a note to a partner) without typing.
 *
 * The SPA derives NO membership here. What the menu may offer is a transient,
 * backend-computed projection (`adviceCreateMenu: { items: [...] } | null`) that
 * rides `GET /sessions/:sessionId/info` alongside `adviceInputRearm` — the same
 * single source of truth on resume and at every turn boundary, so a dissolved
 * couple, an artifact already taken, or a pending intent reconciles on its own.
 * `null` means render nothing; the backend never sends an empty item list.
 *
 * This module owns the descriptor→UI hop and the item→catalog-button resolution
 * shared by the renderer ({@link Input}) and the dispatcher ({@link ActionRouter}).
 *
 * @module core/adviceCreateMenu
 */

import { API } from './api.js';

/**
 * Catalog id of the Create menu's button group. `renderLocation: 'input'` and
 * `showButtonsTool: false` — it is never mounted by `show_buttons`; the menu
 * owns its own panel. The catalog entry exists so `POST /chat/button-interaction`
 * validates the click and reaches the artifact-intent contract branch.
 * @type {string}
 */
export const ADVICE_CREATE_MENU_GROUP_ID = 'advice-create-menu';

/**
 * Descriptor item key → catalog button id. Mirrors the backend group; keep in
 * sync with `button-groups.js`.
 * @type {Object<string,string>}
 */
export const ADVICE_CREATE_MENU_BUTTON_IDS = {
  practice: 'advice-create-practice',
  memo: 'advice-create-memo',
  explore: 'advice-create-explore'
};

/**
 * Descriptor item key → sprite symbol id (minus the `icon-` prefix). Deliberately
 * LOCAL rather than added to `optionsMenu.js`'s shared `MENU_ICON` map: that map is
 * keyed off generic action verbs used by every kebab menu in the app, and
 * `practice`/`memo` are Advice-specific descriptor keys. `renderMenuItems` takes a
 * per-item `icon` override for exactly this case.
 *
 * The symbols match the sidebar's Experiments and Notes entries, so a user meets the
 * same glyph wherever the artifact appears.
 * @type {Object<string,string>}
 */
export const ADVICE_CREATE_MENU_ICONS = {
  practice: 'practices',
  memo: 'mail',
  explore: 'deep-explore'
};

/**
 * Descriptor keys with no catalog button — rendered disabled, never dispatched.
 * `explore_locked` is the PATH_WORK stand-in for the not-yet-available "Deep
 * exploration" item: the backend derives it (never `explore`) whenever
 * `adviceTakeaway == null`, so the locked row always resolves through this
 * local map, disabled, with the hint carried on it. Once a takeaway lands the
 * backend switches to the enabled `explore` token, which resolves through the
 * catalog path below like `practice`/`memo` — `explore` itself is deliberately
 * NOT a key here. `explore_locked` is deliberately absent from
 * `button-groups.js` and from `ADVICE_BUTTON_CONTRACT`: catalog residence in
 * `advice-create-menu` means "resolves to an artifact contract" (see
 * buttonGroups.adviceTerminus.test.js), and a locked row has none — it never
 * dispatches.
 * @type {Object<string,{label:string,icon:string,hint:string}>}
 */
export const ADVICE_CREATE_MENU_LOCAL = {
  explore_locked: {
    label: 'Deep exploration',
    icon: 'deep-explore',
    hint: 'Opens after your key takeaways'
  }
};

/**
 * Resolve descriptor item keys to their catalog buttons, in descriptor order.
 *
 * Labels and actions come from `window.buttonGroupRegistry` (served from the
 * same catalog the acceptance boundary validates against) so the menu can never
 * offer a button the server would reject as unknown. Unknown keys and a missing
 * registry both resolve to an empty list rather than a guessed button.
 *
 * @param {Array<string>|null|undefined} items - Descriptor item keys
 *   ('practice' | 'memo' | 'explore' | 'explore_locked')
 * @returns {Array<{key:string,id:?string,label:string,action:?string,
 *   icon:?string,queuedPlaceholder:?string,disabled:boolean,hint:?string}>}
 *   Resolved items (`id`/`action` are null for local, not-yet-wired rows;
 *   `hint` is null except on a locked row)
 */
export function resolveAdviceCreateMenuItems(items) {
  if (!Array.isArray(items) || items.length === 0) return [];
  const buttons = window.buttonGroupRegistry?.get?.(ADVICE_CREATE_MENU_GROUP_ID)?.buttons;
  if (!Array.isArray(buttons)) return [];

  const resolved = [];
  for (const key of items) {
    const local = ADVICE_CREATE_MENU_LOCAL[key];
    if (local) {
      resolved.push({
        key,
        id: null,
        label: local.label,
        action: null,
        icon: local.icon,
        queuedPlaceholder: null,
        disabled: true,
        hint: local.hint || null
      });
      continue;
    }
    const id = ADVICE_CREATE_MENU_BUTTON_IDS[key];
    const button = id ? buttons.find((b) => b.id === id) : null;
    if (!button?.action || !button?.label) continue;
    resolved.push({
      key,
      id: button.id,
      label: button.label,
      action: button.action,
      icon: ADVICE_CREATE_MENU_ICONS[key] || null,
      // Per-button, not group-level: the two items queue with different copy, and
      // the menu has no pending visual of its own, so this is the only feedback a
      // click during generation produces.
      queuedPlaceholder: button.queuedPlaceholder || null,
      disabled: false,
      hint: null
    });
  }
  return resolved;
}

/**
 * Hand a freshly-read descriptor to the chat input. Safe before the input
 * exists (early boot) and safe with `null` (retracts the control).
 *
 * @param {{items: Array<string>}|null} descriptor - Backend projection, or null
 * @returns {void}
 */
export function applyAdviceCreateMenu(descriptor) {
  window.app?.modules?.input?.setCreateMenu?.(descriptor || null);
}

/**
 * Re-read the descriptor from `GET /sessions/:id/info` and apply it. Used at
 * ADVICE turn boundaries and after a stale-click 409, so membership never
 * outlives the facts it was derived from. Non-fatal: a failed read leaves the
 * current menu standing (the acceptance boundary re-checks eligibility anyway).
 *
 * @param {string|null} sessionId - Live session id
 * @returns {Promise<void>}
 */
export async function refreshAdviceCreateMenu(sessionId) {
  if (!sessionId) {
    applyAdviceCreateMenu(null);
    return;
  }
  try {
    const info = await API.getSessionInfo(sessionId);
    if (!info || info.success === false) return;
    applyAdviceCreateMenu(info.adviceCreateMenu || null);
  } catch (error) {
    console.warn('[adviceCreateMenu] descriptor refresh failed (non-blocking):', error);
  }
}
